import { Link } from 'react-router-dom'
import { useAuth } from '@/shared/auth/AuthProvider'
import { cn } from '@/shared/ui/primitives'
import { SipayLogo, SipayMark } from '@/shared/ui/Brand'
import { ProfileButton } from '@/shared/ui/ProfileButton'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { Icon } from './icons'
import { ModuleSwitcher } from './ModuleSwitcher'
import { QuickSearch } from './QuickSearch'
import type { ModuleDef } from './modules'

/**
 * The one top bar, on Beranda and inside every module.
 *
 * Same controls in the same places everywhere: logo (home), module switcher,
 * then search, notifications, account and logout on the right. People find
 * these by position, so a module never adds, removes or reorders them -- its
 * own navigation belongs in its sidebar. See docs/konvensi-ui.md.
 */
export function TopBar({
  module,
  onOpenMenu,
  className,
}: {
  /** The module being shown, or nothing on Beranda. */
  module?: ModuleDef
  /** Given by a module shell: opens its sidebar as a drawer below lg. */
  onOpenMenu?: () => void
  className?: string
}) {
  const { logout } = useAuth()

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex h-header shrink-0 items-center gap-2 border-b border-line bg-card/90 px-4 backdrop-blur-md sm:gap-3 sm:px-6',
        className,
      )}
    >
      {onOpenMenu && (
        <button
          type="button"
          className="grid size-10 place-items-center rounded-md text-gray-600 hover:bg-gray-100 lg:hidden"
          onClick={onOpenMenu}
          aria-label="Buka menu"
        >
          <Icon name="menu" className="size-6" />
        </button>
      )}

      <Link to="/" title="Beranda" aria-label="Beranda SIPAY" className="shrink-0 rounded-md">
        {/* The mark alone on a phone: the wordmark costs the width the
            switcher needs, and the mark is recognisable on its own. */}
        <SipayMark className="size-8 sm:hidden dark:brightness-0 dark:invert" />
        <SipayLogo className="hidden h-8 sm:block dark:brightness-0 dark:invert" />
      </Link>

      <span aria-hidden="true" className="hidden h-7 w-px bg-line sm:block" />
      <ModuleSwitcher current={module} />

      <div className="ml-auto flex items-center gap-1 sm:gap-4">
        <QuickSearch />
        <NotificationBell />
        <ProfileButton />
        {/* Icon, like the two beside it: logout is the one control on this bar
            nobody is looking for, and a labelled button gave the most
            destructive action the most weight. title carries the tooltip,
            aria-label the accessible name. */}
        <button
          type="button"
          onClick={() => void logout()}
          title="Keluar"
          aria-label="Keluar"
          className={cn(
            'grid size-10 place-items-center rounded-md text-gray-600 transition-colors',
            'hover:bg-danger-light hover:text-danger',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          )}
        >
          <Icon name="logout" className="size-5" />
        </button>
      </div>
    </header>
  )
}
